/**
 * Startup timings, PIN to first menu frame. Each stage is marked once (also
 * as a `pancake:<stage>` Performance mark, so it shows in DevTools' timeline);
 * `window.__PANCAKE_STARTUP__()` prints the table in any build.
 */
export interface StartupStage {
  stage: string;
  /** ms since the page started loading. */
  atMs: number;
  /** ms since the stage before it. */
  deltaMs: number;
}

export class StartupTimeline {
  private readonly marks = new Map<string, number>();

  constructor(private readonly now: () => number = () => performance.now()) {}

  /** First call wins: a stage that happens again (a retry) keeps its first time. */
  mark(stage: string): void {
    if (this.marks.has(stage)) return;
    this.marks.set(stage, this.now());
    try {
      performance.mark(`pancake:${stage}`);
    } catch {
      // Performance marks are optional.
    }
  }

  has(stage: string): boolean {
    return this.marks.has(stage);
  }

  /** ms from one stage to another, or null if either hasn't happened. */
  between(from: string, to: string): number | null {
    const a = this.marks.get(from);
    const b = this.marks.get(to);
    return a === undefined || b === undefined ? null : Math.round(b - a);
  }

  stages(): StartupStage[] {
    let prev: number | null = null;
    return [...this.marks.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([stage, at]) => {
        const row = { stage, atMs: Math.round(at), deltaMs: prev === null ? 0 : Math.round(at - prev) };
        prev = at;
        return row;
      });
  }
}

export const startup = new StartupTimeline();

export function markStartup(stage: string): void {
  startup.mark(stage);
}

declare global {
  interface Window {
    __PANCAKE_STARTUP__?: () => StartupStage[];
  }
}

if (typeof window !== 'undefined') {
  window.__PANCAKE_STARTUP__ = () => {
    const rows = startup.stages();
    console.table(rows);
    return rows;
  };
}
